import { Box, Link, Typography } from "@mui/material";
import type { JSX } from "react";

/**
 * Tiny safe markdown renderer for the strategy memo. The strategist
 * produces a constrained subset (headings ## / ###, paragraphs,
 * bullet/number lists, **bold**, *italic*, `code`, [link](url)) so we
 * don't pull in `react-markdown` + sanitizer + remark-gfm just for one
 * page. Anything we don't recognise renders as escaped text — which is
 * exactly what we want.
 *
 * No raw HTML is allowed through. Links are forced to `target=_blank`
 * with `rel="noreferrer noopener"`. URLs that don't start with http/https
 * become plain text so a model hallucinating javascript: URLs can't
 * inject into a staff browser.
 */
export function StrategyMarkdown({ source }: { source: string }): JSX.Element {
  const blocks = parseBlocks(source);
  return (
    <Box component="article" sx={{ display: "grid", gap: 2, fontSize: "0.9375rem", lineHeight: 1.6, color: "text.primary" }}>
      {blocks.map((b, i) => renderBlock(b, i))}
    </Box>
  );
}

type Block =
  | { kind: "h2"; text: string }
  | { kind: "h3"; text: string }
  | { kind: "p";  text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] };

function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const out: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const raw = lines[i] ?? "";
    const line = raw.trimEnd();
    if (line === "") { i++; continue; }
    if (line.startsWith("### ")) {
      out.push({ kind: "h3", text: line.slice(4).trim() });
      i++;
      continue;
    }
    if (line.startsWith("## ")) {
      out.push({ kind: "h2", text: line.slice(3).trim() });
      i++;
      continue;
    }
    // unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i] ?? "")) {
        items.push((lines[i] ?? "").replace(/^\s*[-*]\s+/, "").trim());
        i++;
      }
      out.push({ kind: "ul", items });
      continue;
    }
    // ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i] ?? "")) {
        items.push((lines[i] ?? "").replace(/^\s*\d+\.\s+/, "").trim());
        i++;
      }
      out.push({ kind: "ol", items });
      continue;
    }
    // paragraph: gather contiguous non-empty, non-special lines
    const buf: string[] = [line];
    i++;
    while (i < lines.length) {
      const next = (lines[i] ?? "").trimEnd();
      if (next === "") break;
      if (next.startsWith("## ") || next.startsWith("### ")) break;
      if (/^\s*[-*]\s+/.test(next) || /^\s*\d+\.\s+/.test(next)) break;
      buf.push(next);
      i++;
    }
    out.push({ kind: "p", text: buf.join(" ") });
  }
  return out;
}

function renderBlock(b: Block, idx: number): JSX.Element {
  switch (b.kind) {
    case "h2":
      return (
        <Typography
          key={idx}
          component="h2"
          sx={{ mt: 1, fontSize: "1.125rem", fontWeight: 600, letterSpacing: "-0.01em", color: "text.primary" }}
        >
          {renderInline(b.text)}
        </Typography>
      );
    case "h3":
      return (
        <Typography
          key={idx}
          component="h3"
          sx={{ fontSize: "0.9375rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}
        >
          {renderInline(b.text)}
        </Typography>
      );
    case "p":
      return (
        <Typography key={idx} component="p" sx={{ color: "text.primary" }}>
          {renderInline(b.text)}
        </Typography>
      );
    case "ul":
      return (
        <Box key={idx} component="ul" sx={{ display: "grid", gap: 0.5, listStyleType: "disc", pl: 2.5, m: 0, color: "text.primary" }}>
          {b.items.map((it, j) => (
            <Box component="li" key={j}>{renderInline(it)}</Box>
          ))}
        </Box>
      );
    case "ol":
      return (
        <Box key={idx} component="ol" sx={{ display: "grid", gap: 0.5, listStyleType: "decimal", pl: 2.5, m: 0, color: "text.primary" }}>
          {b.items.map((it, j) => (
            <Box component="li" key={j}>{renderInline(it)}</Box>
          ))}
        </Box>
      );
  }
}

/** Inline render: handles **bold**, *italic*, `code`, and [text](url). */
function renderInline(text: string): JSX.Element[] {
  const tokens: Array<JSX.Element | string> = [];
  const re = /(\*\*[^*]+\*\*)|(\*[^*]+\*)|(`[^`]+`)|(\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let key = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      tokens.push(text.slice(last, match.index));
    }
    const t = match[0];
    if (t.startsWith("**")) {
      tokens.push(<strong key={key++}>{t.slice(2, -2)}</strong>);
    } else if (t.startsWith("*")) {
      tokens.push(<em key={key++}>{t.slice(1, -1)}</em>);
    } else if (t.startsWith("`")) {
      tokens.push(
        <Box
          component="code"
          key={key++}
          sx={{ borderRadius: 1, bgcolor: "surfaceSoft", px: 0.5, py: 0.25, fontSize: "0.85em" }}
        >
          {t.slice(1, -1)}
        </Box>,
      );
    } else if (t.startsWith("[")) {
      const linkM = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(t);
      if (linkM) {
        const label = linkM[1] ?? "";
        const url = linkM[2] ?? "";
        if (/^https?:\/\//i.test(url)) {
          tokens.push(
            <Link
              key={key++}
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              underline="hover"
              sx={{ color: "primary.main" }}
            >
              {label}
            </Link>,
          );
        } else {
          tokens.push(label);
        }
      }
    }
    last = match.index + t.length;
  }
  if (last < text.length) tokens.push(text.slice(last));
  return tokens.map((t, i) =>
    typeof t === "string" ? <span key={`s-${i}`}>{t}</span> : (
      <span key={`r-${i}`}>{t}</span>
    ),
  );
}
